import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { useIntlShape, useT } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { groupTotal, perCompany, stackTotal, usd as usdIn, type StackData, type StackItem } from "./library";
import { readPref, writePref } from "./prefs";

const KIND: Record<StackItem["kind"], [string, MessageKey]> = {
  free: ["", "library.stack.kind.free"],
  fixed: ["info", "library.stack.kind.fixed"],
  usage: ["warn", "library.stack.kind.usage"],
  revenue: ["danger", "library.stack.kind.revenue"],
};

/** Scale picker with totals, a summary by group at every scale, then the inventory with the picked scale's costs. */
export function StackView({ data }: { data: StackData }) {
  const t = useT();
  const intl = useIntlShape();
  const usd = (value: number, digits?: number) => usdIn(value, digits, intl.locale);
  const pct = (share: number, digits = 0) => intl.formatNumber(share, { style: "percent", maximumFractionDigits: digits, minimumFractionDigits: digits });
  const [scale, setScaleState] = useState(() => {
    const saved = readPref("lib.stack.scale");
    return data.scales.some((s) => s.key === saved) ? (saved as string) : data.scales[0].key;
  });
  const setScale = (key: string) => {
    setScaleState(key);
    writePref("lib.stack.scale", key);
  };
  const picked = data.scales.find((s) => s.key === scale) ?? data.scales[0];
  const total = stackTotal(data, scale);
  return (
    <div className="stack-lg" style={{ marginTop: 16 }}>
      <div className="lib-scale" role="group" aria-label={t("library.stack.scaleLabel")}>
        {data.scales.map((s) => {
          const each = perCompany(data, s.key);
          return (
            <button key={s.key} type="button" aria-pressed={s.key === scale} onClick={() => setScale(s.key)}>
              <span>{s.label}</span>
              <b>{usd(stackTotal(data, s.key))}</b>
              <span>{each !== null ? t("library.stack.perMonthEach", { each: usd(each, 2) }) : t("library.stack.perMonth")}</span>
            </button>
          );
        })}
      </div>
      <p className="lib-small">
        <span className="lib-est">{t("library.est")}</span> {picked.companies}. {picked.note}
        {picked.revenue ? <> {t("library.stack.revenue", { revenue: usd(picked.revenue), share: pct(total / picked.revenue) })}</> : null}
      </p>

      <div>
        <h3>{t("library.stack.byGroup")}</h3>
        <div className="lib-table-wrap">
          <table className="lib-table" style={{ minWidth: 640 }}>
            <thead>
              <tr>
                <th>{t("library.stack.th.group")}</th>
                {data.scales.map((s) => (
                  <th key={s.key} className="num">{s.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.groups.map((g) => (
                <tr key={g.key}>
                  <td className="nm">{g.title}</td>
                  {data.scales.map((s) => (
                    <td key={s.key} className="num">{usd(groupTotal(g, s.key))}</td>
                  ))}
                </tr>
              ))}
              <tr className="lib-total">
                <td>
                  {t("library.stack.total")} <span className="lib-est">{t("library.est")}</span>
                </td>
                {data.scales.map((s) => (
                  <td key={s.key} className="num">{usd(stackTotal(data, s.key))}</td>
                ))}
              </tr>
              <tr>
                <td className="lib-muted">{t("library.stack.perCompany")}</td>
                {data.scales.map((s) => {
                  const each = perCompany(data, s.key);
                  return <td key={s.key} className="num lib-muted">{each === null ? "—" : usd(each, 2)}</td>;
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3>{t("library.stack.inventory", { scale: picked.label })}</h3>
        <p className="lib-small">{t("library.stack.inventoryHint")}</p>
        <div className="lib-table-wrap tall">
          <table className="lib-table" style={{ minWidth: 1180 }}>
            <thead>
              <tr>
                <th>{t("library.stack.th.service")}</th>
                <th>{t("library.stack.th.does")}</th>
                <th>{t("library.stack.th.where")}</th>
                <th>{t("library.stack.th.plan")}</th>
                <th className="num">{t("library.stack.th.monthly")}</th>
                <th>{t("library.stack.th.share")}</th>
                <th>{t("library.stack.th.basis")}</th>
              </tr>
            </thead>
            <tbody>
              {data.groups.map((g) => (
                <Fragment key={g.key}>
                  <tr className="lib-group-row">
                    <td colSpan={4}>{g.title}</td>
                    <td className="num">{usd(groupTotal(g, scale))}</td>
                    <td colSpan={2} />
                  </tr>
                  {g.items.map((item) => {
                    const cost = item.cost[scale] ?? 0;
                    const [tone, label] = KIND[item.kind];
                    return (
                      <tr key={item.name}>
                        <td className="nm">
                          {item.name}
                          <div style={{ marginTop: 4 }}>
                            <span className={`lib-pill ${tone}`}>{t(label)}</span>
                          </div>
                        </td>
                        <td className="wide">
                          {item.does}
                          {item.tech && <div className="lib-small" style={{ marginTop: 4 }}>{item.tech}</div>}
                        </td>
                        <td className="where">{item.where}</td>
                        <td style={{ minWidth: 180 }}>{item.plan}</td>
                        <td className="num"><b>{usd(cost)}</b></td>
                        <td style={{ minWidth: 90 }}>
                          <div className="lib-bar" title={total ? pct(cost / total, 1) : ""}>
                            <i style={{ width: `${total ? Math.max(cost > 0 ? 2 : 0, (cost / total) * 100) : 0}%` }} />
                          </div>
                        </td>
                        <td className="basis">{item.basis}</td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="lib-card">
        <h4>{t("library.stack.assumptions")}</h4>
        <ul className="lib-small" style={{ margin: 0 }}>
          {data.assumptions.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      </div>
      <div>
        <h3>{t("library.stack.sources")}</h3>
        <ol className="lib-small">
          {data.sources.map((s) => (
            <li key={s.label}>
              {s.url.startsWith("/") ? <Link to={s.url}>{s.label}</Link> : <a href={s.url} target="_blank" rel="noopener noreferrer">{s.label}</a>} · {t("library.stack.checked", { date: s.checked })}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
